import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { avatarOf } from '../avatars.js';
import { initials } from '../format.js';
import { busy, error, importPrompt, online, trashWindow, uploadProgress } from '../state.js';
import { processing } from '../derivatives.js';
import { t } from '../i18n.js';
import { IconOffline } from './icons.js';
import { useDialog } from './dialog.js';

/**
 * Les briques communes aux trois destinations.
 *
 * Elles n'ont aucune connaissance du domaine : ce sont des formes, et leur seule
 * responsabilité de fond est **l'accessibilité** — un bouton-icône sans nom, un
 * focus invisible ou une cible de 20 px sont des défauts fonctionnels, pas des
 * finitions.
 */

type ButtonProps = {
  readonly testId: string;
  readonly onClick: () => void;
  readonly variant?: 'primary' | 'quiet' | 'danger';
  readonly children: ComponentChildren;
  readonly title?: string;
  /**
   * **Le nom du bouton quand son libellé peut disparaître** *(2026-08-15)*.
   *
   * Sous 52 rem, plusieurs libellés passent en `.label-wide`, donc en
   * `display: none` — et un texte masqué ainsi n'existe **pas** pour une
   * technologie d'assistance. Le bouton deviendrait alors une icône sans nom, ce
   * que ce fichier interdit depuis l'étape 1. Il pose donc `aria-label` *et*
   * `title` : le premier pour qui écoute, le second pour qui hésite.
   */
  readonly label?: string;
  /** Laisse le bouton actif pendant une mutation. Réservé à ce qui ne mute rien. */
  readonly alwaysEnabled?: boolean;
};

/**
 * Un bouton d'action, **inerte tant qu'une mutation est en vol**.
 *
 * Ce n'est pas un raffinement d'ergonomie : sans cela, deux clics rapides lisent
 * le même état — celui d'avant la première action. Créer un dossier puis y créer
 * une note aussitôt rangeait la note dans le dossier *précédent*, parce que la
 * vue n'avait pas encore bougé. C'est un test qui l'a trouvé, en cliquant plus
 * vite qu'un humain pressé.
 */
export function Action({
  testId,
  onClick,
  variant,
  children,
  title,
  label,
  alwaysEnabled,
}: ButtonProps): JSX.Element {
  return (
    <button
      type="button"
      class={variant ? `btn ${variant}` : 'btn'}
      data-testid={testId}
      aria-label={label}
      title={title ?? label}
      disabled={!alwaysEnabled && Boolean(busy.value)}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * **Un interrupteur, et non plus un bouton-icône** *(2026-08-16)*.
 *
 * Le retour d'usage est sans appel : « le petit icône est incompréhensible et en
 * plus ce n'est pas un *toggle*, on ne voit pas s'il est activé ou pas ; le
 * tooltip lui-même ne marche que sur un PC ». Les trois griefs sont trois défauts
 * distincts, et un seul contrôle les corrige tous :
 *
 * - **le libellé est du texte**, toujours affiché. Un `title` n'existe pas au
 *   doigt : sur téléphone, un bouton-icône dont le sens vit dans l'infobulle n'a
 *   littéralement aucun sens ;
 * - **l'état se voit** — piste pleine et pastille à droite quand c'est ouvert.
 *   `aria-pressed` sur une icône ne se voit que d'un lecteur d'écran ;
 * - **la forme dit qu'on peut basculer**, ce qu'une icône ne dit jamais.
 *
 * `role="switch"` plutôt qu'une case à cocher : c'est un réglage qui prend effet
 * **à l'instant du clic**, sans formulaire ni validation.
 */
export function Switch(props: {
  readonly testId: string;
  readonly checked: boolean;
  readonly label: string;
  /** La conséquence, en une phrase. Elle change avec l'état — c'est tout l'intérêt. */
  readonly hint?: string;
  readonly onChange: (next: boolean) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      class={props.checked ? 'switch on' : 'switch'}
      data-testid={props.testId}
      aria-checked={props.checked}
      disabled={Boolean(busy.value)}
      onClick={() => props.onChange(!props.checked)}
    >
      <span class="switch-track" aria-hidden="true">
        <span class="switch-knob" />
      </span>
      <span class="switch-text">
        <span class="switch-label">{props.label}</span>
        {props.hint ? <span class="switch-hint">{props.hint}</span> : null}
      </span>
    </button>
  );
}

/** Un bouton-icône. `label` **est** son nom accessible, et il n'est pas optionnel. */
export function IconButton(props: {
  readonly testId: string;
  readonly label: string;
  readonly onClick: () => void;
  readonly children: ComponentChildren;
  readonly pressed?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      class="icon-btn"
      data-testid={props.testId}
      aria-label={props.label}
      title={props.label}
      aria-pressed={props.pressed}
      onClick={props.onClick}
    >
      {props.children}
    </button>
  );
}

/**
 * **La confirmation d'un geste qui détruit** *(2026-08-16)*.
 *
 * Elle existe parce que trois gestes partaient au premier clic : supprimer une
 * sélection entière, supprimer un objet depuis son menu, et le retirer d'un
 * partage. Le retour d'usage nomme le mode d'échec exactement — « surtout qu'il
 * n'y a pas de confirmation : suppression immédiate en cas d'appui au mauvais
 * endroit ». Sur un téléphone, où la cible fait 40 px et le pouce autant, ce
 * n'est pas une hypothèse.
 *
 * **Trois règles**, et chacune vient d'un travers observé ailleurs :
 *
 * ① Elle se pose **sur place**, jamais en boîte de dialogue. C'est déjà la forme
 *    retenue pour supprimer un groupe et pour couper un accès (`shared.tsx`,
 *    `admin.tsx`) ; une modale de plus aurait fait deux grammaires du même acte.
 * ② Le bouton porte **le verbe**, pas « OK ». On doit pouvoir décider en lisant
 *    le seul bouton qu'on s'apprête à toucher.
 * ③ L'annulation est **toujours à droite du verbe** et reste active pendant une
 *    mutation (`alwaysEnabled`) : elle ne mute rien, et un bouton d'annulation
 *    grisé pendant qu'une action est en vol est précisément le moment où on
 *    veut l'utiliser.
 *
 * `note` porte la nuance quand le geste en a une — c'est là que « retirer n'est
 * pas supprimer » se lit désormais, plutôt que sous une entrée de menu.
 */
/**
 * **La phrase d'une suppression, et elle ne dit plus « irrécupérable »**
 * *(2026-09-12)*.
 *
 * Le texte affirmait le contraire de ce que le produit fait depuis P2 : une
 * suppression part à la corbeille, où elle reste le temps que le serveur annonce.
 * La page Corbeille le disait déjà, à deux écrans de là.
 *
 * Une confirmation qui exagère le danger se paie **deux fois** : elle retient un
 * geste réversible, et elle décrédibilise les avertissements qui, eux, sont vrais.
 *
 * Deux raisons d'en faire une fonction plutôt que deux appels à `t()` :
 *
 * ① Le nombre de jours est **servi** par `GET /me`, et le repli quand il n'a pas
 *    encore répondu est une phrase **sans nombre** — pas un 30 inventé.
 * ② Le même geste existe dans le menu d'objet **et** dans la fiche. Ce fichier
 *    porte déjà la trace de ce qui arrive quand un geste est écrit deux fois :
 *    « deux implémentations du même geste finissaient par diverger ».
 */
export function deleteQuestion(name: string): string {
  const days = trashWindow.value;
  return days === undefined
    ? t('space.confirmDeleteSoft', { name })
    : t('space.confirmDelete', { name, days });
}

/** La même règle, pour une sélection. `count` porte le pluriel, `days` la durée. */
export function deletePickedQuestion(count: number): string {
  const days = trashWindow.value;
  return days === undefined
    ? t('gallery.confirmDeletePickedSoft', { count })
    : t('gallery.confirmDeletePicked', { count, days });
}

export function Confirm(props: {
  readonly testId: string;
  /** La question. Elle nomme ce sur quoi elle porte. */
  readonly question: string;
  /** Le verbe du geste, sur le bouton qui l'exécute. */
  readonly verb: string;
  /** La nuance, quand il y en a une. */
  readonly note?: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}): JSX.Element {
  return (
    <div class="confirm" data-testid={props.testId} role="group" aria-label={props.question}>
      <span class="confirm-text">
        {props.question}
        {props.note ? <span class="confirm-note">{props.note}</span> : null}
      </span>
      <Action testId={`${props.testId}-yes`} variant="danger" onClick={props.onConfirm}>
        {props.verb}
      </Action>
      <Action
        testId={`${props.testId}-no`}
        variant="quiet"
        onClick={props.onCancel}
        alwaysEnabled
      >
        {t('app.cancel')}
      </Action>
    </div>
  );
}

/**
 * **« x doublons ont été trouvés et ne seront pas importés »** *(K2, DB2)*.
 *
 * **Une modale, et non une ligne en haut de page** *(retour d'usage, 2026-09-13)* :
 * posée sous la barre comme `Notice`, la question se lisait comme un message parmi
 * d'autres, alors que l'import attend la réponse.
 *
 * Deux gestes et pas d'annulation : la sélection est déjà faite, et la seule
 * question est de savoir ce qu'on fait des doublons. « OK » est le défaut — il
 * envoie les autres, et c'est aussi ce que fait `Échap` ; « Importer quand même »
 * envoie tout. Posée **hors** de `busy`, sans quoi ses propres boutons seraient
 * désactivés.
 */
export function ImportPromptDialog(): JSX.Element | null {
  const prompt = importPrompt.value;
  const host = useDialog(prompt !== undefined, { onEscape: () => prompt?.choose(false) });
  // **Le focus va au geste par défaut**, pas au premier bouton : `useDialog` le
  // pose sur « Importer quand même », et `Entrée` aurait alors forcé l'import de
  // tous les doublons. Cet effet est déclaré après, donc il passe après.
  const skip = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (prompt) skip.current?.querySelector<HTMLButtonElement>('button:last-child')?.focus();
  }, [prompt]);
  if (!prompt) return null;
  return (
    <div
      class="overlay"
      ref={host}
      tabIndex={-1}
      data-testid="import-duplicates"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="import-duplicates-title"
      aria-describedby="import-duplicates-text"
    >
      <div class="sheet sheet-narrow">
        <h2 id="import-duplicates-title">{t('import.duplicatesTitle')}</h2>
        <p id="import-duplicates-text" class="sheet-text">
          {t('import.duplicates', { count: prompt.duplicates })}
          {prompt.inTrash > 0 ? (
            <span class="sheet-note">{t('import.duplicatesInTrash', { count: prompt.inTrash })}</span>
          ) : null}
        </p>
        <div class="sheet-actions" ref={skip}>
          <Action testId="import-duplicates-force" variant="quiet" onClick={() => prompt.choose(true)} alwaysEnabled>
            {t('import.force')}
          </Action>
          <Action testId="import-duplicates-skip" variant="primary" onClick={() => prompt.choose(false)} alwaysEnabled>
            {t('import.skipDuplicates')}
          </Action>
        </div>
      </div>
    </div>
  );
}

/**
 * L'attente et l'erreur, **au même endroit et jamais toutes les deux**.
 *
 * Un écran qui montre en même temps « chargement… » et une erreur de l'action
 * précédente laisse choisir laquelle croire.
 */
export function Notice(): JSX.Element | null {
  if (busy.value) {
    const bytes = uploadProgress.value;
    return (
      <p class="notice busy" data-testid="busy" role="status">
        <span class="spinner" aria-hidden="true" />
        {busy.value}
        {/* **Un `<progress>` natif, et non une barre dessinée** : il porte son rôle
            et sa valeur pour les lecteurs d'écran sans qu'on ait à les écrire, et
            il reste lisible sous un thème forcé. Il ne paraît que pendant un envoi
            d'octets — sur toutes les autres attentes, il n'y a **rien** à
            annoncer, et une barre sans valeur connue est pire qu'aucune barre. */}
        {bytes && bytes.total > 0 ? (
          <progress
            class="upload-progress"
            data-testid="upload-progress"
            max={bytes.total}
            value={bytes.sent}
          />
        ) : null}
      </p>
    );
  }
  if (error.value) {
    return (
      <p class="notice error" data-testid="error" role="alert">
        {error.value}
      </p>
    );
  }
  return null;
}

/**
 * **Ce qui se construit encore, dit sans rien bloquer** *(2026-09-12)*.
 *
 * Elle répond au grief exact : « on voit l'upload de chaque élément puis à la fin
 * *traitement en cours* […] on attend sans rien voir bouger ». L'ancienne phrase
 * était portée par `busy`, qui **désactive tous les boutons** — donc un mur muet
 * qui interdisait de faire autre chose.
 *
 * Celle-ci n'est **pas** dans `Notice` : elle n'est ni une attente ni une erreur,
 * et surtout elle doit pouvoir cohabiter avec les deux. Un envoi qui échoue pendant
 * qu'un dérivé se construit doit montrer l'échec **et** le compte.
 *
 * Le nombre **descend**, ce qui est la seule chose qu'on veut savoir : quelque
 * chose avance. Les tuiles concernées portent déjà leur propre sablier.
 */
export function ProcessingNotice(): JSX.Element | null {
  const left = processing.value;
  if (left === 0) return null;
  return (
    <p class="notice pending" data-testid="processing" data-left={left} role="status">
      <span class="spinner" aria-hidden="true" />
      {t('space.processingCount', { count: left })}
    </p>
  );
}

/** Le bandeau hors-ligne. Il ne s'affiche que quand il a quelque chose à dire. */
export function OfflineBanner(): JSX.Element | null {
  if (online.value) return null;
  return (
    <p class="offline" data-testid="offline" role="status">
      <IconOffline size={16} />
      {t('state.offline')}
    </p>
  );
}

/**
 * L'état vide — **une phrase, et de quoi en sortir**.
 *
 * Un vide sans explication se lit comme une panne. Le `testId` reste `empty`
 * partout : c'est le même état, quelle que soit la destination.
 */
export function Empty(props: {
  readonly children: ComponentChildren;
  readonly action?: ComponentChildren;
  readonly as?: 'li' | 'div';
}): JSX.Element {
  const Tag = (props.as ?? 'div') as 'div';
  return (
    <Tag class="empty" data-testid="empty">
      <span>{props.children}</span>
      {props.action}
    </Tag>
  );
}

/**
 * Une pastille d'information : effectif, rôle, état. Jamais cliquable.
 *
 * `share` n'est pas une nuance d'`accent` : c'est **l'exposition** — ce qui a
 * quitté l'espace privé, ou ce que d'autres ont posé dessus. La question que la
 * V2 prétend rendre visible mérite une teinte à elle, qui ne soit ni celle de
 * l'action ni celle du risque.
 */
export function Pill(props: {
  readonly children: ComponentChildren;
  readonly tone?: 'neutral' | 'accent' | 'share' | 'warning' | 'danger';
  readonly testId?: string;
}): JSX.Element {
  return (
    <span class={`pill ${props.tone ?? 'neutral'}`} data-testid={props.testId}>
      {props.children}
    </span>
  );
}

/**
 * **Un visage, ou des initiales** — jamais un trou.
 *
 * Trois états, et le troisième est celui qui compte : l'image quand elle est là,
 * les initiales quand la personne n'a pas d'avatar, et **les initiales aussi**
 * pendant que l'URL se résout. Un vide qui se remplit fait sauter la ligne ; des
 * initiales que l'image recouvre ne bougent rien, puisque la boîte a déjà sa
 * taille.
 *
 * Elles servent aussi de repli quand les octets ne se chargent pas — URL présignée
 * expirée pendant qu'un onglet dormait, par exemple. Une image cassée à la place
 * d'un visage se lit comme un bogue ; des initiales, non.
 */
export function Avatar({
  uid,
  alias,
  rev,
  big = false,
  testId,
}: {
  readonly uid: string;
  readonly alias: string | undefined;
  readonly rev: number | undefined;
  readonly big?: boolean;
  readonly testId?: string;
}): JSX.Element {
  const [broken, setBroken] = useState(false);
  const url = avatarOf(uid, rev);
  const cls = big ? 'avatar big' : 'avatar';

  if (url && !broken) {
    return (
      <img
        class={`${cls} avatar-image`}
        data-testid={testId}
        src={url}
        alt=""
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <span class={cls} data-testid={testId} aria-hidden="true">
      {initials(alias)}
    </span>
  );
}
